import React, { useState, useEffect, useRef } from 'react';
import logo from '../assets/logo/Track my day logo.png';
import DemoModal from './DemoModal';

const SLOGAN = 'Log your day. Claim your way.';

function HomePage({ onInloggen, onRegistreren, onDemo, demoOpen = false, onDemoClose }) {
  const [typed, setTyped] = useState('');
  const [done, setDone] = useState(false);
  const audioCtxRef = useRef(null);
  const silentRef = useRef(false);

  const getCtx = () => {
    if (!audioCtxRef.current)
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return audioCtxRef.current;
  };

  const playTick = async () => {
    try {
      const ctx = getCtx();
      if (ctx.state === 'suspended') { silentRef.current = true; return; }
      const dur = 0.03;
      const bufSize = Math.floor(ctx.sampleRate * dur);
      const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let n = 0; n < bufSize; n++) data[n] = (Math.random() * 2 - 1) * Math.pow(1 - n / bufSize, 2);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 1100;
      filter.Q.value = 0.8;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.7, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
      src.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);
      src.start();
    } catch (_) {}
  };

  const runTypewriter = (interval) => {
    let i = 0;
    setTyped('');
    setDone(false);
    const timer = setInterval(() => {
      i++;
      setTyped(SLOGAN.slice(0, i));
      playTick();
      if (i >= SLOGAN.length) { clearInterval(timer); setDone(true); }
    }, interval);
    return () => clearInterval(timer);
  };

  // Eerste keer: start automatisch na 600ms
  useEffect(() => {
    const delay = setTimeout(() => runTypewriter(55), 600);
    return () => clearTimeout(delay);
  }, []);

  // Bij eerste klik: unlock audio, en herspeel als geluiden gemist werden
  useEffect(() => {
    const unlock = async () => {
      const ctx = getCtx();
      if (ctx.state === 'suspended') await ctx.resume();
      if (silentRef.current) {
        silentRef.current = false;
        runTypewriter(30);
      }
    };
    document.addEventListener('click', unlock, { once: true });
    return () => document.removeEventListener('click', unlock);
  }, []);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#172554' }}>
      {demoOpen && <DemoModal onClose={onDemoClose} />}

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="flex flex-col items-center space-y-10 w-full max-w-sm">
          <img
            src={logo}
            alt="Track My Day"
            className="h-40 w-auto bg-white rounded-2xl px-8 py-4 shadow-xl"
          />

          <p className="text-white text-center text-lg font-semibold tracking-wide min-h-[2rem]">
            {typed}
            {!done && <span className="animate-blink ml-0.5 text-blue-300">|</span>}
          </p>

          <div className="flex flex-col w-full space-y-4">
            <button
              onClick={onDemo}
              className="w-full flex items-center justify-center gap-3 bg-white text-blue-900 font-semibold py-4 px-6 rounded-xl shadow-lg hover:bg-blue-50 transition transform hover:scale-105"
            >
              <svg className="w-5 h-5 text-blue-700 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Bekijk de demo video
            </button>

            <button
              onClick={onRegistreren}
              className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-4 px-6 rounded-xl shadow-lg transition transform hover:scale-105"
            >
              Registreren
            </button>

            <button
              onClick={onInloggen}
              className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold py-4 px-6 rounded-xl shadow-lg transition transform hover:scale-105 border border-white/20"
            >
              Inloggen
            </button>
          </div>
        </div>
      </div>

      <footer className="py-4 text-center text-xs text-blue-300">
        © 2026 Powered by <a href="https://www.novarockets.nl" target="_blank" rel="noopener noreferrer" className="hover:text-blue-100 underline">Nova Rockets</a>
      </footer>
    </div>
  );
}

export default HomePage;
